/**
 * ImpersonationBanner
 *
 * Faixa amarela fixa no topo de toda a aplicação (incluindo /activities/focus
 * que roda em modo `noShell` no AppLayout). Visível apenas quando há sessão de
 * impersonation ativa. Mostra nome do usuário-alvo, tempo restante até o TTL
 * expirar, e botão para sair manualmente.
 */

import { AlertTriangle, LogOut, Loader2 } from 'lucide-react';
import { useImpersonation } from '@/contexts/ImpersonationContext';

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0min';
  const totalMinutes = Math.ceil(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h${String(minutes).padStart(2, '0')}` : `${hours}h`;
}

export function ImpersonationBanner() {
  const { state, isImpersonating, exit, remainingMs, busy } = useImpersonation();

  if (!isImpersonating || !state) return null;

  const name = state.target.full_name || state.target.email;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[100] w-full bg-yellow-400 text-black border-b border-yellow-600 shadow-sm"
    >
      <div className="flex items-center gap-3 px-4 py-1.5 text-xs font-medium">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span className="flex-1 min-w-0 truncate">
          Visualizando como <strong>{name}</strong>
          <span className="hidden sm:inline">
            {' '}· {state.target.email}
          </span>
          <span className="ml-2 text-black/70">
            · expira em {formatRemaining(remainingMs)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => void exit()}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-black/10 hover:bg-black/20 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 text-xs font-semibold transition-colors"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <LogOut className="h-3.5 w-3.5" aria-hidden />
          )}
          Sair da impersonation
        </button>
      </div>
    </div>
  );
}

